import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit2, Trash2, Eye, AlertCircle, Search, Archive, RotateCcw } from 'lucide-react';
import { flyersService } from '../../services/flyersService';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { formatDate } from '../../utils/helpers';
import { useAuthStore } from '../../store/authStore';
import {
  Zalozka,
  jeUzavrenyArchivProdejce,
  porovnejLetaky,
  sedneNaZalozku,
} from '../../utils/letakZalozky';

/**
 * Záložky rozdělují letáky podle stavu. Nahradily dřívější dvojici
 * Aktivní/Archiv doplněnou o filtrační tlačítka pod hledáním; stejnou informaci
 * nesl i štítek u každého letáku, takže se říkala třikrát.
 *
 * Které stavy kam spadají a jak se uvnitř záložky řadí, je v utils/letakZalozky
 * - společně s přehledem pro admina a schvalovatele, aby se pravidla nerozešla.
 * Zdejší jsou jen popisky, ty se mezi rolemi liší.
 */
const ZALOZKY: { key: Zalozka; label: string }[] = [
  { key: 'active', label: 'Aktivní letáky' },
  { key: 'draft', label: 'Koncepty' },
  { key: 'ceka', label: 'Čeká na schválení' },
  { key: 'rejected', label: 'Zamítnuté' },
  { key: 'archive', label: 'Archiv' },
  // Smazané se nenabízejí - mazání je trvalé. Měkké mazání bylo zamítnuto
  // v 09/2026 (docs/processed/navrh-smazane-letaky.md).
];

/** Záložky prodejce: jeho leták se nikdy neodesílá, zná jen koncept a archiv. */
const ZALOZKY_PRODEJCE: { key: Zalozka; label: string }[] = [
  { key: 'draft', label: 'Aktuální letáky' },
  { key: 'archive', label: 'Archiv' },
];

/** Parametr ?status= z dlaždice na Přehledu -> odpovídající záložka. */
const zalozkaZUrl = (status: string | null): Zalozka | null => {
  if (status === 'active' || status === 'draft' || status === 'rejected') return status;
  if (status === 'pending_approval' || status === 'pending_verification' || status === 'approved') return 'ceka';
  if (status === 'expired') return 'archive';
  return null;
};

export const FlyersListPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const currentUserId = useAuthStore((s) => s.user?.id);
  const currentUserRole = useAuthStore((s) => s.user?.role);
  const isEndUser = currentUserRole === 'end_user';
  const [activeTab, setActiveTab] = useState<Zalozka>('active');
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const flyerRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const statusFilter = searchParams.get('status');
  const highlightFlyerId = searchParams.get('highlight');

  // Determine base path based on current location
  const isMyFlyers = location.pathname.startsWith('/my-flyers');
  const basePath = isMyFlyers ? '/my-flyers' : '/flyers';

  const zalozky = isEndUser ? ZALOZKY_PRODEJCE : ZALOZKY;

  // Proklik z dlaždice na Přehledu rovnou otevře odpovídající záložku.
  useEffect(() => {
    const zUrl = zalozkaZUrl(statusFilter);
    if (zUrl && zalozky.some((z) => z.key === zUrl)) setActiveTab(zUrl);
    else if (isEndUser) setActiveTab('draft');
  }, [statusFilter, isEndUser]); // eslint-disable-line react-hooks/exhaustive-deps

  const { data: flyers = [], isLoading } = useQuery({
    queryKey: ['flyers', 'my'],
    queryFn: () => flyersService.getMyFlyers(),
  });

  const pocty = useMemo(() => {
    const m = {} as Record<Zalozka, number>;
    for (const z of zalozky) m[z.key] = flyers.filter((f) => sedneNaZalozku(f, z.key, isEndUser)).length;
    return m;
  }, [flyers, zalozky, isEndUser]);

  // Filter flyers by tab (active vs archive) and status filter
  const filteredByTab = useMemo(
    () => flyers.filter((f) => sedneNaZalozku(f, activeTab, isEndUser)).sort(porovnejLetaky),
    [flyers, activeTab, isEndUser],
  );

  // Filter by search query
  const filteredFlyers = useMemo(() => {
    if (!searchQuery.trim()) {
      return filteredByTab;
    }
    const query = searchQuery.toLowerCase();
    return filteredByTab.filter(f =>
      f.name.toLowerCase().includes(query) ||
      formatDate(f.validFrom).toLowerCase().includes(query) ||
      formatDate(f.validTo).toLowerCase().includes(query)
    );
  }, [filteredByTab, searchQuery]);

  // Scroll to and highlight flyer when returning from edit
  useEffect(() => {
    if (highlightFlyerId && !isLoading && filteredFlyers.length > 0) {
      const flyerElement = flyerRefs.current[highlightFlyerId];
      if (flyerElement) {
        // Wait a bit for rendering to complete
        setTimeout(() => {
          flyerElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setHighlightedId(highlightFlyerId);

          // Remove highlight after 3 seconds
          setTimeout(() => {
            setHighlightedId(null);
          }, 3000);
        }, 100);
      }
    }
  }, [highlightFlyerId, isLoading, filteredFlyers]);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => flyersService.deleteFlyer(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['flyers'] });
    },
    onError: (error: any) => {
      alert(error?.response?.data?.message || 'Leták se nepodařilo smazat');
    },
  });

  const handleDelete = async (id: string, name: string) => {
    if (window.confirm(`Opravdu chcete smazat "${name}"?`)) {
      await deleteMutation.mutateAsync(id);
    }
  };

  // Prodejce: archivace vlastního konceptu (→ Archiv) a obnovení zpět (→ Aktuální).
  const archiveMutation = useMutation({
    mutationFn: (id: string) => flyersService.archiveFlyer(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['flyers'] }),
    onError: (e: any) => alert(e?.response?.data?.message || 'Leták se nepodařilo archivovat'),
  });
  const unarchiveMutation = useMutation({
    mutationFn: (id: string) => flyersService.unarchiveFlyer(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['flyers'] }),
    onError: (e: any) => alert(e?.response?.data?.message || 'Leták se nepodařilo obnovit'),
  });

  const handleArchive = (id: string, name: string) => {
    const varovani =
      'Archivace je nevratná – leták už nepůjde vrátit mezi aktuální, ' +
      'zobrazit ani stáhnout jeho PDF.';
    if (window.confirm(`Přesunout leták "${name}" do archivu?\n\n${varovani}`)) {
      archiveMutation.mutate(id);
    }
  };
  const handleUnarchive = (id: string) => unarchiveMutation.mutate(id);

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  // Nadpis je stálý - co je právě vidět, říká vybraná záložka.
  const getTitle = () => (isMyFlyers ? 'Moje letáky' : 'Letáky');
  const getSubtitle = () => 'Spravujte své propagační letáky';

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{getTitle()}</h1>
          <p className="mt-2 text-gray-600">{getSubtitle()}</p>
        </div>
        <Button onClick={() => navigate(`${basePath}/new`)}>
          <Plus className="w-4 h-4 mr-2" />
          Nový leták
        </Button>
      </div>

      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex flex-wrap gap-x-6">
          {zalozky.map((z) => {
            const vybrana = activeTab === z.key;
            return (
              <button
                key={z.key}
                onClick={() => setActiveTab(z.key)}
                className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                  vybrana
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {z.label}
                <span
                  className={`ml-2 py-0.5 px-2 rounded-full text-xs ${
                    vybrana ? 'bg-blue-100 text-blue-700' : 'bg-gray-100'
                  }`}
                >
                  {pocty[z.key] ?? 0}
                </span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Search bar */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <Input
            type="text"
            placeholder="Hledat podle názvu nebo data platnosti..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {filteredFlyers.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <div className="text-gray-400 mb-4">
            {searchQuery ? (
              <Search className="w-16 h-16 mx-auto" />
            ) : (
              <Plus className="w-16 h-16 mx-auto" />
            )}
          </div>
          {searchQuery ? (
            <>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Žádné výsledky</h3>
              <p className="text-gray-600 mb-6">Zkuste změnit vyhledávací dotaz</p>
              <Button variant="outline" onClick={() => setSearchQuery('')}>
                Vymazat vyhledávání
              </Button>
            </>
          ) : activeTab === 'archive' ? (
            <>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Žádné vypršelé letáky</h3>
              <p className="text-gray-600">Archiv je prázdný</p>
            </>
          ) : activeTab === 'deleted' ? (
            <>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Žádné smazané letáky</h3>
              <p className="text-gray-600">Smazané letáky se tu zatím neuchovávají.</p>
            </>
          ) : activeTab === 'draft' ? (
            <>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Žádné koncepty</h3>
              <p className="text-gray-600 mb-6">Začněte vytvořením prvního letáku</p>
              <Button onClick={() => navigate(`${basePath}/new`)}>
                <Plus className="w-4 h-4 mr-2" />
                Vytvořit leták
              </Button>
            </>
          ) : activeTab === 'ceka' ? (
            <>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Nic nečeká na schválení</h3>
              <p className="text-gray-600">Odeslané letáky se objeví tady.</p>
            </>
          ) : activeTab === 'rejected' ? (
            <>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Žádné zamítnuté letáky</h3>
              <p className="text-gray-600">Letáky vrácené k opravě se objeví tady.</p>
            </>
          ) : (
            <>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Žádné aktivní letáky</h3>
              <p className="text-gray-600">Schválené letáky v období platnosti se objeví tady.</p>
            </>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow divide-y">
          {filteredFlyers.map(flyer => (
            <div
              key={flyer.id}
              ref={(el) => {
                flyerRefs.current[flyer.id] = el;
              }}
              className={`p-6 transition-all duration-300 ${
                highlightedId === flyer.id
                  ? 'bg-blue-50 border-2 border-blue-500'
                  : 'hover:bg-gray-50'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-3 mb-2 min-w-0">
                    {/* Stav neopakujeme štítkem - říká ho vybraná záložka. */}
                    <h3 className="text-lg font-semibold truncate min-w-0" title={flyer.name}>{flyer.name}</h3>
                  </div>
                  <div className="text-sm text-gray-600 space-y-1">
                    <div>Platnost: {formatDate(flyer.validFrom)} - {formatDate(flyer.validTo)}</div>
                    <div>Stránky: {flyer.pages?.length || 0} | Produkty: {flyer.productCount ?? (flyer.pages?.reduce((s, p) => s + (p.slots?.filter(slot => slot && slot.type === 'product').length || 0), 0) || 0)}</div>
                    <div>Vytvořeno: {formatDate(flyer.createdAt)}</div>
                  </div>

                  {/* Rejection reason for rejected flyers */}
                  {flyer.rejectionReason && (
                    <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
                      <div className="flex items-start space-x-2">
                        <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-red-900 mb-1">Důvod zamítnutí:</p>
                          <p className="text-sm text-red-800 whitespace-pre-wrap break-words">
                            {flyer.rejectionReason}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex space-x-2 flex-shrink-0">
                  {flyer.status === 'draft' && !flyer.rejectionReason && flyer.supplierId === currentUserId && (
                    <>
                      <Button size="sm" onClick={() => navigate(`${basePath}/${flyer.id}`)}>
                        <Edit2 className="w-4 h-4 mr-1" />
                        Upravit
                      </Button>
                      {/* Prodejce: archivace vlastního konceptu (leták nikdy není „aktivní"). */}
                      {isEndUser && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleArchive(flyer.id, flyer.name)}
                          isLoading={archiveMutation.isPending}
                          disabled={archiveMutation.isPending}
                          title="Přesunout leták do Archivu – zmizí z Aktuálních a zpět už ho vrátit nepůjde"
                        >
                          <Archive className="w-4 h-4 mr-1" />
                          Archivovat
                        </Button>
                      )}
                      <button
                        onClick={() => handleDelete(flyer.id, flyer.name)}
                        title="Smazat leták"
                        className="text-red-600 hover:text-red-900 p-2 rounded hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                  {flyer.status === 'draft' && flyer.rejectionReason && flyer.supplierId === currentUserId && (
                    <>
                      <Button size="sm" onClick={() => navigate(`${basePath}/${flyer.id}`)} className="bg-orange-600 hover:bg-orange-700">
                        <Edit2 className="w-4 h-4 mr-1" />
                        Opravit a odeslat znovu
                      </Button>
                      <button
                        onClick={() => handleDelete(flyer.id, flyer.name)}
                        title="Smazat leták"
                        className="text-red-600 hover:text-red-900 p-2 rounded hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                  {(flyer.status !== 'draft' || flyer.supplierId !== currentUserId) && (
                    <Button size="sm" variant="outline" onClick={() => navigate(`${basePath}/${flyer.id}`)}>
                      <Eye className="w-4 h-4 mr-1" />
                      Zobrazit
                    </Button>
                  )}
                  {/* Prodejce: obnovit vlastní archivovaný leták zpět do konceptu.
                      Po uplynutí platnosti (tedy po archivaci vždy) se nenabízí. */}
                  {isEndUser &&
                    flyer.status === 'expired' &&
                    flyer.supplierId === currentUserId &&
                    !jeUzavrenyArchivProdejce(flyer, isEndUser) && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleUnarchive(flyer.id)}
                      isLoading={unarchiveMutation.isPending}
                      disabled={unarchiveMutation.isPending}
                      title="Vrátit leták z Archivu zpět mezi Aktuální jako koncept"
                    >
                      <RotateCcw className="w-4 h-4 mr-1" />
                      Obnovit
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
